import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import LegalEntitySchema from "legal-entities/_schema/legal-entity-schema";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import LegalEntity from "legal-entities/legal-entity";
import { z } from "zod";

export type OpenEntityMemberInvitationJSON = z.infer<
  typeof OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationJSONZ
>;
export type OpenEntityMemberInvitationParams = z.infer<
  typeof OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationZ
>;

export default class OpenEntityMemberInvitationSchema {
  public static readonly OpenEntityMemberInvitationJSONZ = z.object({
    id: z.uuid().nullish(),
    entityId: z.uuid(),
    entityName: z.string(),
    expires: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly OpenEntityMemberInvitationZ = z.object({
    id: z.instanceof(Guid).optional(),
    entity: z.instanceof(LegalEntity),
    expires: z.instanceof(AHDate).optional(),
  });

  public static validate(values: unknown): OpenEntityMemberInvitationParams {
    return parseOrThrow(OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationZ, values, {
      logError: true,
      schemaName: "OpenEntityMemberInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): OpenEntityMemberInvitationJSON {
    return parseOrThrow(OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "OpenEntityMemberInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is OpenEntityMemberInvitationParams {
    try {
      OpenEntityMemberInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is OpenEntityMemberInvitationJSON {
    try {
      OpenEntityMemberInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: OpenEntityMemberInvitationJSON): OpenEntityMemberInvitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "OpenEntityMemberInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return OpenEntityMemberInvitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: OpenEntityMemberInvitationParams): OpenEntityMemberInvitationJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = OpenEntityMemberInvitationSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return OpenEntityMemberInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationJSONZ,
    OpenEntityMemberInvitationSchema.OpenEntityMemberInvitationZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        entity: new LegalEntity(
          LegalEntitySchema.deserialize({
            id: data.entityId,
            name: data.entityName,
          }),
        ),
        expires: AHDate.fromJSON(data.expires) ?? undefined,
      }),
      encode: (entity) => ({
        id: entity.id?.toJSON(),
        entityId: entity.entity.id.toJSON(),
        entityName: EntityNameSchema.validateJSON(entity.entity.name?.value),
        expires: entity.expires?.toJSON(),
      }),
    },
  );
}
